<script setup lang="ts">
import { SancareOcticon, SancarePasswordInput } from '@sancare/typescript-commons'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'

const MIN_PASSWORD_LENGTH = 8

interface FormData {
  oldPassword: string
  password: string
  passwordConfirmation: string
  firstName: string
  lastName: string
}

interface FormErrors {
  passwordMismatch: boolean
  passwordChars: boolean
  passwordTooShort: boolean
}

const { t } = useI18n()
const store = useStore()

const showSettings = ref(false)
const teamModeUpdate = ref(false)
const INITIAL_FORM_DATA = {
  oldPassword: '',
  password: '',
  passwordConfirmation: '',
  firstName: '',
  lastName: '',
}

const INITIAL_FORM_ERRORS = {
  passwordMismatch: false,
  passwordChars: false,
  passwordTooShort: false,
}

const formData = reactive<FormData>({ ...INITIAL_FORM_DATA })
const formErrors = reactive<FormErrors>({ ...INITIAL_FORM_ERRORS })

const currentUser = computed(() => store.state.login.currentUser)
const updateSelfRequest = computed(() => store.state.login.updateSelfRequest)

const teamMode = computed(() => store.state.login.currentUser.teamMode)
const handleTeamModeToggle = async (value: boolean) => {
  teamModeUpdate.value = true
  try {
    await store.dispatch('updateSelf', { teamMode: value })
  } finally {
    teamModeUpdate.value = false
  }
}

watch(showSettings, (visible) => {
  if (visible) {
    document.body.classList.add('modal-open')
  } else {
    document.body.classList.remove('modal-open')
  }
})

watch(updateSelfRequest, (req) => {
  if (!req.ok) {
    return
  }
  if (teamModeUpdate.value) {
    teamModeUpdate.value = false
    return
  }
  closeModal()
})

watch(() => formData.password, (newPassword) => {
  if (formErrors.passwordTooShort && newPassword.length >= MIN_PASSWORD_LENGTH) {
    formErrors.passwordTooShort = false
  }
  if (formErrors.passwordMismatch && newPassword === formData.passwordConfirmation) {
    formErrors.passwordMismatch = false
  }
  if (formErrors.passwordChars && newPassword.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)) {
    formErrors.passwordChars = false
  }
})

watch(() => formData.passwordConfirmation, (newConfirmation) => {
  if (formErrors.passwordMismatch && formData.password === newConfirmation) {
    formErrors.passwordMismatch = false
  }
})

watch(currentUser, (newUser) => {
  formData.firstName = newUser.firstName
  formData.lastName = newUser.lastName
})

function closeModal() {
  showSettings.value = false
  formData.oldPassword = ''
  formData.password = ''
  formData.passwordConfirmation = ''
  formData.firstName = currentUser.value.firstName
  formData.lastName = currentUser.value.lastName
  Object.assign(formData, INITIAL_FORM_DATA)

  formErrors.passwordTooShort = false
  formErrors.passwordMismatch = false
  formErrors.passwordChars = false
  Object.assign(formErrors, INITIAL_FORM_ERRORS)
  store.commit('resetUpdateSelf')
}

function openModal() {
  showSettings.value = true
  store.dispatch('getSelf')
}

function checkPasswords() {
  formErrors.passwordTooShort = formData.password.length < MIN_PASSWORD_LENGTH
  formErrors.passwordChars = !formData.password.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)
  if (formData.password && formData.passwordConfirmation && formData.password !== formData.passwordConfirmation) {
    formErrors.passwordMismatch = true
  }
}

function handleSubmitPassword() {
  checkPasswords()
  if (formErrors.passwordTooShort || formErrors.passwordChars || formErrors.passwordMismatch) {
    return
  }

  const payload = {
    password: formData.password,
    oldPassword: formData.oldPassword,
  }
  store.dispatch('updateSelf', payload)
}

function handleSubmitNames() {
  const payload: Record<string, string> = {}
  if (formData.firstName !== currentUser.value.firstName) {
    payload.firstName = formData.firstName
  }
  if (formData.lastName !== currentUser.value.lastName) {
    payload.lastName = formData.lastName
  }
  store.dispatch('updateSelf', payload)
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <span>
    <transition name="sancare-modal">
      <div
        v-if="showSettings"
        class="sancare-modal-mask row g-0 justify-content-center align-items-start"
        @click.stop="closeModal()"
      >
        <div
          class="sancare-modal-overlay col-10 col-md-7 col-lg-5 col-xl-4"
          @click.stop
        >
          <div class="sancare-modal-header">
            <span class="row">
              <span
                class="sancare-modal-icon col-1 col-md-1 col-lg-1 col-xl-1"
              >
                <sancare-octicon
                  name="gear"
                  :width="18"
                  :height="26"
                />
              </span>
              <span class="sancare-modal-title col-9 col-md-9 col-lg-9 col-xl-9">{{
                $t('parameters')
              }}</span>
              <span
                class="sancare-modal-close col-2 col-md-2 col-lg-2 col-xl-2"
                @click="closeModal()"
              >
                <sancare-octicon
                  name="x"
                  :width="20"
                  :height="20"
                />
              </span>
            </span>
          </div>
          <div class="sancare-modal-body">
            <form
              class="row"
              @submit.prevent="handleSubmitNames"
            >
              <div class="mb-3 col-12 col-md-6">
                <label
                  for="first-name"
                  name="first-name"
                  class="small-label"
                >
                  {{ $t('user.firstName') }}
                </label>
                <input
                  id="first-name"
                  v-model="formData.firstName"
                  class="form-control"
                >
              </div>
              <div class="mb-3 col-12 col-md-6">
                <label
                  for="last-name"
                  name="last-name"
                  class="small-label"
                >
                  {{ $t('user.lastName') }}
                </label>
                <input
                  id="last-name"
                  v-model="formData.lastName"
                  class="form-control"
                >
              </div>
              <div class="col-12 text-end">
                <button
                  class="btn btn-push btn-primary btn-padded"
                  type="submit"
                >
                  {{ $t('settings.userSettings.updateInfo') }}
                </button>
              </div>
            </form>
            <hr v-if="currentUser.createdFromLdap === false">
            <form
              v-if="currentUser.createdFromLdap === false"
              class="row"
              @submit.prevent="handleSubmitPassword"
            >
              <div class="mb-3 col-6">
                <label
                  for="old-password"
                  name="old-password"
                  class="small-label"
                >
                  {{ $t('settings.userSettings.oldPassword') }}
                </label>
                <sancare-password-input
                  id="old-password"
                  v-model="formData.oldPassword"
                  required
                />
              </div>
              <div class="col-6" />
              <div class="form-group col-12 col-md-6">
                <label
                  for="password"
                  name="password"
                  class="small-label"
                >
                  {{ $t('settings.userSettings.newPassword') }}
                </label>
                <sancare-password-input
                  id="password"
                  v-model="formData.password"
                  :class="{ 'is-invalid': formErrors.passwordTooShort || formErrors.passwordChars }"
                  :minlength="MIN_PASSWORD_LENGTH"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$"
                  :title="$t('settings.userSettings.passwordChars')"
                  required
                  @blur="checkPasswords"
                />
                <div class="invalid-feedback">
                  <div v-if="formErrors.passwordTooShort">
                    {{ $t('settings.userSettings.passwordTooShort') }}
                  </div>
                  <div v-if="formErrors.passwordChars">
                    {{ $t('settings.userSettings.passwordChars') }}
                  </div>
                </div>
              </div>
              <div class="mb-3 col-12 col-md-6">
                <label
                  for="passwordConfirmation"
                  name="password-confirm"
                  class="small-label"
                >
                  {{ $t('confirmation') }}
                </label>
                <sancare-password-input
                  id="passwordConfirmation"
                  v-model="formData.passwordConfirmation"
                  :class="{ 'is-invalid': formErrors.passwordMismatch }"
                  :minlength="MIN_PASSWORD_LENGTH"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$"
                  :title="$t('settings.userSettings.passwordChars')"
                  required
                  @blur="checkPasswords"
                />
                <div class="invalid-feedback">
                  {{ $t('settings.userSettings.passwordMismatch') }}
                </div>
              </div>
              <div class="col-12 text-end">
                <button
                  class="btn btn-push btn-primary btn-padded"
                  type="submit"
                >
                  {{ $t('settings.userSettings.updatePassword') }}
                </button>
              </div>
            </form>
            <div
              v-if="updateSelfRequest.error"
              class="text-danger"
            >
              {{ updateSelfRequest.error }}
            </div>
            <hr v-if="currentUser.team !== null">
            <div
              v-if="currentUser.team !== null"
              class="row g-0 justify-content-center align-items-center"
            >
              <v-dropdown
                :triggers="['hover']"
                placement="top"
                popover-class="onmodal"
                style="margin: 0;padding: 0;margin-bottom: 5px;"
                class="col-1"
              >
                <span class="d-block text-center mx-auto">
                  <sancare-octicon
                    name="question"
                    :width="16"
                    :height="30"
                  />
                </span>
                <slot />
                <template #popper>
                  <div style="z-index: 10000;">
                    {{ $t('settings.userSettings.teamModeHelp') }}
                  </div>
                </template>
              </v-dropdown>
              <label
                class="col-form-label col-6 text-center toggle-switch"
                for="inTeamModeBool"
                style="align-self: center;margin: 0;"
              >
                {{ $t('settings.userSettings.teamMode') }}
              </label>
              <div class="col-1">
                <input
                  id="inTeamModeBool"
                  name="teamMode"
                  type="checkbox"
                  :checked="teamMode"
                  style="margin: 0;"
                  class=""
                  @change="handleTeamModeToggle(($event.target as HTMLInputElement).checked)"
                >
              </div>
            </div>
          </div>
          <div class="sancare-modal-footer" />
        </div>
      </div>
    </transition>
  </span>
</template>
